import Label from 'types/Label';
import { ChecklistSeverityEnum, Checklist } from 'types/Checklist';
import { Position } from 'types/Position';
import GenericQuestion from 'types/Question';
import QuestionForm from 'types/QuestionForm';
import Media, { MediaInterface } from 'types/Media';
import { AbstractMedia } from 'types/AbstractMedia';
import { Policy } from 'modules/auth/types/Policies';
import { Dealer } from 'modules/dealers/types/Dealer';
import { KeyStatus } from 'modules/kiosk/types/Kiosk';
import RestrictedAccess from 'types/RestrictedAccess';
import { KioskPreference } from 'types/KioskPreference';
import { Item } from 'modules/vehicleCheck/types/VehicleCheckModel';
import { OpenKeysSafeResponse } from 'modules/kiosk/types/KeysSafe';
import { CustomerInfo } from 'modules/selfServices/types/CustomerInfo';
import { KioskConnectionStatus } from 'modules/dealers/types/ContextResponse';

import SelfServiceStatus from './SelfServiceStatus';
import { SelfServiceSteps } from 'modules/selfServices/types/SelfServiceSteps';

export enum BreakdownServiceAuthorType {
  CUSTOMER = 'CUSTOMER',
  BREAKDOWN_MECHANIC = 'BREAKDOWN_MECHANIC',
}

export const QUICK_CHECK_IN = 'QUICK_CHECK_IN';

export enum SelfServiceType {
  CHECK_IN = 'CHECK_IN',
  CHECK_OUT = 'CHECK_OUT',
  BREAKDOWN = 'BREAKDOWN',
}

export enum SelfServiceOrigin {
  HOME = 'HOME',
  KIOSK = 'KIOSK',
  ON_SITE = 'ON_SITE',
}

export enum FinalInvoiceStatus {
  CREATED = 'CREATED',
  PAID = 'PAID',
  SENT = 'SENT',
}

export enum FuelLevel {
  FULL = 'FULL',
  THREE_QUARTERS = 'THREE_QUARTERS',
  HALF = 'HALF',
  ONE_QUARTER = 'ONE_QUARTER',
  RESERVE = 'RESERVE',
}

export enum DocumentStatus {
  ACCEPTED = 'ACCEPTED',
  REQUESTED = 'REQUESTED',
  TO_VALIDATE = 'TO_VALIDATE',
  REFUSED = 'REFUSED',
}

export enum SignatureStatus {
  SIGNED = 'SIGNED',
  UNSIGNED = 'UNSIGNED',
}

export enum FlowType {
  CLASSIC = 'CLASSIC',
  QUICK = 'QUICK',
}

export const AllSelfServiceTypes = Object.values(SelfServiceType) as SelfServiceType[];

export type FinalInvoice = {
  id: string;
  payable: boolean;
  authorId: string;
  createdOn: string;
  enabled: boolean;
  name: string;
  link?: string;
  price: number;
  status: FinalInvoiceStatus;
  medias?: AbstractMedia[];
  pending: boolean;
  // The statuses of the last PAYMENT_REQUEST
  failed: boolean;
  aborted: boolean;
};

// Property computed on our side to display easily a repairOrder
interface RepairOrderUtils {
  currency?: string;
  translatedName?: string,
  translatedDescription?: string,
  vat?: number;
}

export interface RepairOrder extends RepairOrderUtils {
  adviceName: Label;
  price?: number;
  discountedPrice?: number;
  coefficient: number;
  description?: Label;
  payerId?: string;
  workOrderTag?: string;
  severity?: ChecklistSeverityEnum;
}

export interface Maps {
  id: string;
  media: AbstractMedia;
  name: string;
  status: string;
}

interface ParkingMap {
  allowParkingSpot: boolean;
  maps?: Maps[];
  selectedMaps?: Record<string, string>;
}

export interface Parking {
  mapId?: string;
  x?: number;
  y?: number;
  spotNumber?: string;
}

interface Vehicle {
  color?: string;
  description?: string;
  fuelLevel?: FuelLevel;
  medias?: AbstractMedia[];
  mileage?: number;
  parking?: Parking;
  registration?: string;
}

export interface MobilityDocument {
  id: string;
  accepted: boolean;
  name: Label;
  media?: Media;
  comment?: string;
  status?: DocumentStatus;
}

interface ContractSignature {
  page: number;
  topLeft: Position;
  bottomRight: Position;
}

export interface BasicContract {
  id: string;
  name?: Label;
  signatureBox?: ContractSignature;
  media?: AbstractMedia;
  status?: SignatureStatus;
}

export interface Mobility {
  id?: string;
  vehicle?: Vehicle;
  parkingReturn?: Parking;
  notPrepared?: boolean;
  selfServiceId?: string;
  contract?: BasicContract;
  documents?: MobilityDocument[];
  customerAccepted?: boolean;
  deliveryDocument?: BasicContract;
  courtesyVehicleCheckEnabled?: boolean;
}

interface VehicleCheck {
  id: string;
  name: string;
  items: Item[];
}

interface SignatureBox {
  bottomRight: Position;
  page: number;
  topLeft: Position;
}

interface WorkOrder {
  media: Media;
  signatureBox: SignatureBox;
  status: SignatureStatus;
}

interface PreAuthorizedAmounts {
  CUSTOMER?: number,
  LEASER?: number,
}

export interface Payer {
  id: string,
  name: Label,
}

export type ConfiguredWorkflows = { [type in SelfServiceType | typeof QUICK_CHECK_IN] : SelfServiceSteps[] };

export interface SelfService {
  id: string;
  customerComment?: string;
  repairOrder?: RepairOrder[];
  checklists?: Array<Checklist>;
  proactiveChecklists?: Checklist[];
  customerInfo?: CustomerInfo;
  dealer: Dealer;
  documents?: BasicContract[];
  keyStatus?: KeyStatus;
  questionForm?: QuestionForm;
  medias?: MediaInterface[];
  signature?: Pick<MediaInterface, 'uploadId'>;
  valueAddedTax: number;
  finalInvoice?: FinalInvoice;
  interventionMedias?: AbstractMedia[];
  /** @description `true` when summary signature is enabled AND there was modifications between HOME flow and the current one */
  isSummarySignatureNeeded?: boolean;
  comment?: string;
  parkingMap?: ParkingMap;
  parking?: Parking;
  type: SelfServiceType;
  elapsedTime?: number;
  status: SelfServiceStatus;
  origin: SelfServiceOrigin;
  originContext?: {
    [origin in SelfServiceOrigin]?: {
      elapsedTime?: number;
      status: SelfServiceStatus;
    };
  };
  invoicePaymentMandatory?: boolean;
  slot?: OpenKeysSafeResponse;
  vehicleCheckAccepted?: boolean;
  courtesyVehicleCheckAccepted?: boolean;
  legalNotice?: string;
  mobilityEnabled?: boolean;
  mobility?: Mobility;
  payers?: Payer[];
  breakdownServiceEmail?: string;
  vehicleCheck?: VehicleCheck;
  courtesyVehicleCheck?: VehicleCheck;
  workOrder?: WorkOrder;
  kioskPreference?: KioskPreference;
  preAuthorizedAmounts?: PreAuthorizedAmounts,
  kioskConnectionStatus?: KioskConnectionStatus,
  kioskPreferencesQuestions?: Array<GenericQuestion>;
  breakdownServiceAuthorType?: BreakdownServiceAuthorType,
  reopen?: {
    until?: string;
    allowed: boolean;
  },
  isQuick?: boolean,
  policies?: Array<Policy>;
  kioskRestrictedAccess?: RestrictedAccess,
}

export interface PhoneNumber {
  local: string;
  prefix: string;
}

export const isEqualPhoneNumber = (a: PhoneNumber, b: PhoneNumber) => a?.prefix === b?.prefix && a?.local === b?.local;
